import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../utils/api'

function SavingsGoals() {
  const { getToken } = useAuth()
  const [goals, setGoals] = useState([])
  const [summary, setSummary] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    current_amount: '',
    target_date: ''
  })
  const [editingGoal, setEditingGoal] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [goalsData, summaryData] = await Promise.all([
        api.get('/api/goals', getToken),
        api.get('/api/summary', getToken)
      ])
      setGoals(goalsData)
      setSummary(summaryData)
    } catch (error) {
      console.error('Error loading data:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name || !formData.target_amount) {
      alert('Please enter a goal name and target amount')
      return
    }

    setLoading(true)
    try {
      await api.post('/api/goals', {
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        current_amount: parseFloat(formData.current_amount) || 0,
        target_date: formData.target_date || null
      }, getToken)

      setFormData({ name: '', target_amount: '', current_amount: '', target_date: '' })
      loadData()
    } catch (error) {
      console.error('Error adding goal:', error)
      alert('Failed to add goal')
    }
    setLoading(false)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    
    if (!editingGoal) return

    setLoading(true)
    try {
      await api.put('/api/goals', {
        id: editingGoal.id,
        name: editingGoal.name,
        target_amount: parseFloat(editingGoal.target_amount),
        current_amount: parseFloat(editingGoal.current_amount),
          target_date: editingGoal.target_date || null
        }, getToken)

      setEditingGoal(null)
      loadData()
    } catch (error) {
      console.error('Error updating goal:', error)
      alert('Failed to update goal')
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this savings goal?')) return

    try {
      await api.delete(`/api/goals?id=${id}`, getToken)
      loadData()
    } catch (error) {
      console.error('Error deleting goal:', error)
    }
  }

  const calculateMonthsToGoal = (goal) => {
    if (!summary || summary.potentialSavings <= 0) return null
    const remaining = goal.target_amount - goal.current_amount
    return Math.ceil(remaining / summary.potentialSavings)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Savings Goals</h2>
        {summary && (
          <div className="text-right">
            <div className="text-sm text-gray-600">Monthly Savings Potential</div>
            <div className={`text-2xl font-bold ${summary.potentialSavings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${summary.potentialSavings.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      {/* Add New Goal */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Goal</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., New Car, Vacation, Emergency Fund"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.target_amount}
                onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Amount
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.current_amount}
                onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Date (optional)
              </label>
              <input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Adding...' : 'Add Goal'}
          </button>
        </form>
      </div>

      {/* Goals List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {goals.length === 0 ? (
          <div className="col-span-2 bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">🎯</div>
            <p className="text-gray-500">No savings goals yet. Create your first goal above!</p>
          </div>
        ) : (
          goals.map((goal) => {
            const progress = (goal.current_amount / goal.target_amount) * 100
            const remaining = goal.target_amount - goal.current_amount
            const monthsToGoal = calculateMonthsToGoal(goal)
            const isEditing = editingGoal?.id === goal.id

            return (
              <div key={goal.id} className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-primary-500">
                {isEditing ? (
                  <form onSubmit={handleUpdate} className="space-y-3">
                    <input
                      type="text"
                      value={editingGoal.name}
                      onChange={(e) => setEditingGoal({ ...editingGoal, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg font-semibold"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        step="0.01"
                        value={editingGoal.target_amount}
                        onChange={(e) => setEditingGoal({ ...editingGoal, target_amount: e.target.value })}
                        placeholder="Target"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={editingGoal.current_amount}
                        onChange={(e) => setEditingGoal({ ...editingGoal, current_amount: e.target.value })}
                        placeholder="Current"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <input
                      type="date"
                      value={editingGoal.target_date || ''}
                      onChange={(e) => setEditingGoal({ ...editingGoal, target_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 text-sm"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingGoal(null)}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{goal.name}</h3>
                        {goal.target_date && (
                          <p className="text-sm text-gray-500 mt-1">
                            Target: {new Date(goal.target_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingGoal({ ...goal })}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(goal.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>${goal.current_amount.toFixed(2)}</span>
                        <span>${goal.target_amount.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-gradient-to-r from-primary-500 to-primary-600 h-4 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                      <div className="mt-2 text-center">
                        <span className="text-2xl font-bold text-primary-600">{progress.toFixed(1)}%</span>
                        <span className="text-sm text-gray-500"> complete</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-500">Remaining</p>
                        <p className="text-lg font-semibold text-gray-900">${remaining.toFixed(2)}</p>
                      </div>
                      {monthsToGoal && (
                        <div>
                          <p className="text-xs text-gray-500">Months to Goal</p>
                          <p className="text-lg font-semibold text-gray-900">
                            ~{monthsToGoal} {monthsToGoal === 1 ? 'month' : 'months'}
                          </p>
                        </div>
                      )}
                    </div>

                    {summary && summary.potentialSavings > 0 && (
                      <div className="mt-4 bg-primary-50 rounded-lg p-3">
                        <p className="text-xs text-primary-700">
                          💡 At your current savings rate of ${summary.potentialSavings.toFixed(2)}/month, 
                          you'll reach this goal in {monthsToGoal} {monthsToGoal === 1 ? 'month' : 'months'}!
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default SavingsGoals

